import React from 'react';
import c from 'classnames';
import Loader from './Loader';

interface Props {
    type: 'WARN' | 'INFO';
    body: React.ReactNode;
    confirmButtonText: string;
    confirmButtonStyle?: 'button' | 'text';
    onConfirm: () => void;
    onCancel?: () => void;
    confirmDisabled?: boolean;
    state?: 'READY' | 'SAVING';
    width?: 'FULL' | 'HALF';
}

export default function ModalBody({
    body,
    type,
    confirmButtonText: confirmButton,
    onConfirm,
    onCancel,
    confirmDisabled = false,
    state = 'READY',
    confirmButtonStyle = 'button',
}: Props) {
    return (
        <>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 text-sm text-gray-500">{body}</div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                    type="button"
                    disabled={confirmDisabled || state === 'SAVING'}
                    className={c({
                        'w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white  focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm':
                            confirmButtonStyle === 'button',
                        'bg-red-600 hover:bg-red-700 focus:ring-red-500':
                            type === 'WARN' && confirmButtonStyle === 'button',
                        'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500':
                            type === 'INFO' && confirmButtonStyle === 'button',
                        'text-sm': confirmButtonStyle === 'text',
                        'opacity-50': confirmDisabled || state === 'SAVING',
                    })}
                    onClick={onConfirm}
                >
                    {state === 'SAVING' && <Loader small />}
                    {state === 'READY' && confirmButton}
                </button>
                {onCancel && (
                    <button
                        type="button"
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-wedgewood-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                        onClick={onCancel}
                    >
                        Cancel
                    </button>
                )}
            </div>
        </>
    );
}

export function ModalHeader({ type, title }: Pick<Props, 'type'> & { title: string }) {
    return (
        <div className="sm:flex sm:items-center">
            <div
                className={c(
                    'hidden mx-auto shrink-0 sm:flex items-center justify-center h-12 w-12 rounded-full sm:mx-0 sm:h-10 sm:w-10',
                    {
                        'bg-red-100': type === 'WARN',
                        'bg-blue-100': type === 'INFO',
                    }
                )}
            >
                {type === 'WARN' && (
                    <svg
                        className="h-6 w-6 text-red-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                    </svg>
                )}
                {type === 'INFO' && (
                    <svg viewBox="0 0 24 24" className="h-6 w-6 text-blue-600 ">
                        <path
                            fill="currentColor"
                            d="M12,0A12,12,0,1,0,24,12,12.013,12.013,0,0,0,12,0Zm.25,5a1.5,1.5,0,1,1-1.5,1.5A1.5,1.5,0,0,1,12.25,5ZM14.5,18.5h-4a1,1,0,0,1,0-2h.75a.25.25,0,0,0,.25-.25v-4.5a.25.25,0,0,0-.25-.25H10.5a1,1,0,0,1,0-2h1a2,2,0,0,1,2,2v4.75a.25.25,0,0,0,.25.25h.75a1,1,0,1,1,0,2Z"
                        ></path>
                    </svg>
                )}
            </div>
            <h3 className="sm:ml-4 sm:text-lg md:text-2xl leading-6 font-medium text-gray-900">{title}</h3>
        </div>
    );
}
